import { trStatic } from "../i18n/static";

/**
 * SEPA-/ISO-20022-Rücklastschriftgründe (Core Direct Debit Return Reason Codes) als statische
 * Konstanten. Quelle der Wahrheit für Gültigkeit + Sortierung; die Domänenlogik (BlocksFutureDebits)
 * liegt im Backend (Domain/Finance/ChargebackReasonCodes.cs). Anzeigenamen kommen ausschließlich aus i18n.
 *
 * Wird ein Code ergänzt/geändert, muss der Backend-Katalog (ChargebackReasonCodes.cs) + die
 * i18n-Keys ("Chargeback reason <CODE>") nachgezogen werden.
 * Der manuell auswählbare Bank-Klartext ist separat: siehe docs/chargeback-reason-catalog.md.
 */
export const CHARGEBACK_REASON_CODES: readonly string[] = [
  "AC01", "AC04", "AC06", "AC13", "AG01", "AG02", "AM04", "AM05",
  "BE05", "FF01", "FF05", "MD01", "MD02", "MD06", "MD07", "MS02",
  "MS03", "RC01", "RR01", "RR02", "RR03", "RR04", "SL01", "TM01"
];

/**
 * Kundenverständlicher Klartext für die manuelle Erfassung einer Bankrückgabe.
 * Technische Datei-/Verfahrensgründe bleiben im vollständigen Katalog für Bestandsdaten.
 * MS03 enthält keine Aussage zur verdeckten Ursache; MS02 (Ablehnung bei Vorlage)
 * bleibt wegen der unterschiedlichen Sperrfolge von MS03 getrennt.
 * Quellen, Auswahlgrenze und Zuordnung: docs/chargeback-reason-catalog.md.
 */
export const MANUAL_CHARGEBACK_REASON_CATALOG = [
  { code: "MS03", labelKey: "Manual chargeback reason MS03" },
  { code: "MS02", labelKey: "Manual chargeback reason MS02" },
  { code: "MD06", labelKey: "Manual chargeback reason MD06" },
  { code: "MD01", labelKey: "Manual chargeback reason MD01" },
  { code: "MD02", labelKey: "Manual chargeback reason MD02" },
  { code: "AC01", labelKey: "Manual chargeback reason AC01" },
  { code: "AC04", labelKey: "Manual chargeback reason AC04" },
  { code: "AC06", labelKey: "Manual chargeback reason AC06" },
  { code: "AG01", labelKey: "Manual chargeback reason AG01" },
  { code: "SL01", labelKey: "Manual chargeback reason SL01" },
  { code: "AM04", labelKey: "Manual chargeback reason AM04" },
  { code: "AM05", labelKey: "Manual chargeback reason AM05" }
] as const;

const BLOCKS_FUTURE_DEBITS = new Set([
  "AC01", "AC04", "AC06", "AC13", "AG01", "MD01", "MD02", "MD06",
  "MD07", "MS02", "RR01", "RR02", "RR03", "RR04", "SL01"
]);

/** Rein für die Bedienoberfläche; das Backend erzwingt diese Regel unabhängig davon. */
export function chargebackReasonBlocksFutureDebits(code?: string | null): boolean {
  return BLOCKS_FUTURE_DEBITS.has((code ?? "").trim().toUpperCase());
}

/** i18n-Schlüssel je Code (DE/EN in translations.*.ts hinterlegt). */
function labelKeyFor(code: string): string {
  return MANUAL_CHARGEBACK_REASON_CATALOG.find((reason) => reason.code === code)?.labelKey
    ?? `Chargeback reason ${code}`;
}

/** Lokalisierter Name eines Codes; unbekannter Code → der Code selbst. */
export function chargebackReasonLabel(code?: string | null): string {
  const c = (code ?? "").trim().toUpperCase();
  if (!c) return "";
  if (CHARGEBACK_REASON_CODES.includes(c)) return trStatic(labelKeyFor(c));
  return c;
}

/** Technische Anzeige "CODE – Name" für Bankumsätze; unabhängig vom manuellen Klartext. */
export function chargebackReasonCodeAndLabel(code?: string | null): string {
  const c = (code ?? "").trim().toUpperCase();
  if (!c) return "";
  const name = CHARGEBACK_REASON_CODES.includes(c) ? trStatic(`Chargeback reason ${c}`) : c;
  return name && name !== c ? `${c} – ${name}` : c;
}

/** Auswahl ohne Codes; nach übersetztem Klartext sortiert, damit Sprachwechsel sofort wirken. */
export function getChargebackReasonOptions(translate: (key: string) => string, lang: "de" | "en") {
  return MANUAL_CHARGEBACK_REASON_CATALOG.map(({ code, labelKey }) => ({
    value: code,
    label: translate(labelKey)
  })).sort((a, b) => a.label.localeCompare(b.label, lang));
}
